/**
 * The top bar's "2 notices" status item and its popover: every notice a feature published
 * (see notices.ts), loudest first, each with what is going on, why it matters, and the action
 * that fixes it. Hidden when there is nothing to say.
 */
import { BellOff, BellRing, CircleAlert, GitCompareArrows, Info, Radar, Siren, TriangleAlert } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Button, ErrorState, Popover, PopoverContent, PopoverTrigger, cn } from '@orgus/ui';
import { noticesLabel, sortNotices, topTone, useNoticeStore } from './notices.ts';
import type { Notice, NoticeAction, NoticeKind, NoticeTone } from './notices.ts';

const TONE_TEXT: Record<NoticeTone, string> = {
  danger: 'text-status-danger',
  attention: 'text-status-attention',
  neutral: 'text-fg-subtle',
};

const TONE_WORD: Record<NoticeTone, string> = { danger: 'Problem', attention: 'Needs attention', neutral: 'For information' };

const KIND_ICON: Record<NoticeKind, ReactNode> = {
  build: <GitCompareArrows aria-hidden />,
  alerts: <BellRing aria-hidden />,
  'alerts-off': <BellOff aria-hidden />,
  info: <Info aria-hidden />,
  incident: <Siren aria-hidden />,
  watch: <Radar aria-hidden />,
};

/** The small dot on the trigger: only for a problem or something to act on, never a filled badge. */
const TRIGGER_DOT: Record<NoticeTone, string | null> = {
  danger: 'bg-status-danger-fill',
  attention: 'bg-status-attention-fill',
  neutral: null,
};

const TRIGGER_ICON: Record<NoticeTone, ReactNode> = {
  danger: <CircleAlert aria-hidden />,
  attention: <TriangleAlert aria-hidden />,
  neutral: <Info aria-hidden />,
};

/** One notice in the popover: icon in its tone, title, why and what next, the build ids, actions. */
export function NoticeCard({ notice, onNavigate }: { notice: Notice; onNavigate: (to: string) => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<unknown>(null);
  const act = async (a: NoticeAction) => {
    if (a.to) {
      onNavigate(a.to);
      return;
    }
    if (!a.run) return;
    setFailed(null);
    setBusy(a.id);
    try {
      await a.run();
    } catch (e) {
      setFailed(e);
    } finally {
      setBusy(null);
    }
  };
  const error = failed ?? notice.error;
  return (
    <li className="flex gap-3 px-4 py-3" data-notice={notice.id} data-tone={notice.tone}>
      <span className={cn('flex h-5 shrink-0 items-center [&_svg]:size-4', TONE_TEXT[notice.tone])}>{KIND_ICON[notice.kind]}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-ui font-medium text-fg">
          <span className="sr-only">{TONE_WORD[notice.tone]}: </span>
          {notice.title}
        </p>
        <p className="text-caption text-fg-muted">{notice.body}</p>
        {notice.detail && <p className="break-all font-mono text-caption text-fg-subtle">{notice.detail}</p>}
        {notice.actions && notice.actions.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {notice.actions.map((a) => (
              <Button key={a.id} size="sm" variant={a.emphasis ?? 'ghost'} loading={busy === a.id} onClick={() => void act(a)}>
                {a.label}
              </Button>
            ))}
          </div>
        )}
        {error ? <ErrorState variant="inline" error={error} className="mt-1" /> : null}
      </div>
    </li>
  );
}

/** The list without the trigger, for the popover and for tests. */
export function NoticeList({ notices, onNavigate }: { notices: readonly Notice[]; onNavigate: (to: string) => void }) {
  return (
    <ul className="flex max-h-128 flex-col divide-y divide-border-subtle overflow-y-auto">
      {notices.map((n) => (
        <NoticeCard key={n.id} notice={n} onNavigate={onNavigate} />
      ))}
    </ul>
  );
}

/**
 * "2 notices" in the top bar: a quiet icon and count, in the chrome's grey. The loudest tone shows
 * only as a small dot on the icon (red where something failed), so the chrome never outshouts the
 * page. The word drops below 768 px.
 */
export function NoticesItem() {
  const byId = useNoticeStore((s) => s.byId);
  const notices = useMemo(() => sortNotices(Object.values(byId)), [byId]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const tone = topTone(notices);
  const label = noticesLabel(notices.length);
  return (
    <>
      {/* Present from the first paint so a notice arriving later is announced once, politely. */}
      <span aria-live="polite" aria-atomic="true" className="sr-only">
        {notices.length > 0 ? label : ''}
      </span>
      {tone && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              aria-label={label}
              data-notices={notices.length}
              className="shrink-0 animate-fade-in text-fg-muted hover:text-fg"
              icon={
                <span className="relative flex text-fg-subtle [&_svg]:size-4">
                  {TRIGGER_ICON[tone]}
                  {TRIGGER_DOT[tone] && <span aria-hidden className={cn('absolute -right-0.5 -top-0.5 size-1.5 rounded-full ring-2 ring-bg', TRIGGER_DOT[tone])} />}
                </span>
              }
            >
              <span className="num">
                {notices.length}
                <span className="hidden md:inline"> {notices.length === 1 ? 'notice' : 'notices'}</span>
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="flex w-96 max-w-(--radix-popover-content-available-width) flex-col" aria-label="Notices">
            <header className="flex h-row items-center gap-2 px-4">
              <h2 className="text-ui font-medium text-fg">Notices</h2>
              <span className="num text-caption text-fg-subtle">{notices.length}</span>
            </header>
            <NoticeList
              notices={notices}
              onNavigate={(to) => {
                setOpen(false);
                navigate(to);
              }}
            />
          </PopoverContent>
        </Popover>
      )}
    </>
  );
}
